import type { CartFeatureSummary, SelectedCartFeature } from "@/lib/product-features";

export interface CartItemData {
  id: string;
  item_id: string;
  name: string;
  name_ar: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  customizations: {
    size: string | null;
    extras: string[];
    removed_ingredients: string[];
    special_instructions: string;
    selected_features: SelectedCartFeature[];
    feature_summary: CartFeatureSummary[];
  };
}

export interface BranchStatusData {
  is_open: boolean;
  is_busy: boolean;
  reason?: string | null;
}

export interface CartData {
  cart_id: string;
  is_new: boolean;
  items: CartItemData[];
  subtotal: number;
  discount: number;
  delivery_fee: number;
  is_delivery_free: boolean;
  tax: number;
  total: number;
  items_count: number;
  promo_code: string | null;
  restaurant_id: string | null;
  discount_enabled: boolean;
  created_at: string;
  expires_at: string;
  branch_status?: BranchStatusData | null;
}

export interface CartResolveResult {
  success: boolean;
  cartId: string | null;
  cartData: CartData | null;
  isNew: boolean;
  error: { code: string; message: string } | null;
}

export async function resolveCart(cartId?: string | null, restaurantId?: string | null,branchId?: string | null,order_type?: string | null,lat?: number | null,lon?: number | null,phone_number?: string | null): Promise<CartResolveResult> {
  try {
    //const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseUrl = window.location.origin;
    const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

    if (!supabaseUrl || !anonKey) {
      return {
        success: false,
        cartId: null,
        cartData: null,
        isNew: false,
        error: { code: "CONFIG_ERROR", message: "Missing backend configuration" },
      };
    }

    const params = new URLSearchParams();
    if (cartId) params.set("cart_id", cartId);
    if (restaurantId) params.set("restaurant_id", restaurantId);
    if (branchId) params.set("branch_id", branchId);
    if (order_type) params.set("order_type", order_type);
    if (lat) params.set("lat", lat.toString());
    if (lon) params.set("lon", lon.toString());
    if (phone_number) params.set("phone_number", phone_number);

    const qs = params.toString();
    //const url = `${supabaseUrl}/functions/v1/cart-resolve${qs ? `?${qs}` : ""}`;

    const url = `${window.location.origin}/api/WebOrder/functions/v1/cart-resolve${qs ? `?${qs}` : ""}`;
    
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10_000);

    const response = await fetch(url, {
      method: "GET",
      headers: {
        Accept: "application/json",
        apikey: anonKey,
        Authorization: `Bearer ${anonKey}`,
      },
      signal: controller.signal,
    });

    clearTimeout(timeout);

    const json = await response.json();

    if (response.ok && json.success) {
      return {
        success: true,
        cartId: json.data.cart_id,
        cartData: json.data,
        isNew: json.data.is_new,
        error: null,
      };
    }

    return {
      success: false,
      cartId: null,
      cartData: null,
      isNew: false,
      error: json.error || { code: "UNKNOWN", message: "Unexpected response" },
    };
  } catch (err: any) {
    return {
      success: false,
      cartId: null,
      cartData: null,
      isNew: false,
      error: {
        code: err.name === "AbortError" ? "TIMEOUT" : "NETWORK_ERROR",
        message: err.message || "Network request failed",
      },
    };
  }
}
